import { ResponseSuccess } from '@forwardflow/types'
import { apiPost } from './Axios/configs'
import { UserService } from './UserService'

export type HandoffPurpose =
   | 'payment'
   | 'fub-connection'
   | 'calendar-connection'

export interface RedeemedHandoff {
   id: string
   organizationId: string
   userId: number
   purpose: HandoffPurpose
   onboardingRevision: number
   expiresAt: string
   /** Whether setup moved between the link being made and being opened. */
   onboardingChangedSince: boolean
}

export class OnboardingHandoffService {
   /** Tab-local navigation context, never authority: recheck the signed-in person on return. */
   static async open(handoffId: string) {
      const key = `onboarding-handoff:${handoffId}`
      const cached = sessionStorage.getItem(key)
      if (cached) {
         try {
            const data = JSON.parse(cached) as RedeemedHandoff
            if (data.id !== handoffId || !Number.isFinite(Date.parse(data.expiresAt)) ||
                Date.parse(data.expiresAt) <= Date.now()) throw new Error('expired')
            const user = await UserService.getUserData()
            if (!data.userId || !user?.id || String(user.id) !== String(data.userId)) throw new Error('wrong-person')
            return { data }
         } catch (error) {
            sessionStorage.removeItem(key)
            throw error
         }
      }
      const response = await this.redeem(handoffId)
      if (response?.data) sessionStorage.setItem(key, JSON.stringify(response.data))
      return response
   }
   /**
    * Redeem a handoff reference, ONCE, as the signed-in person.
    *
    * The reference is navigation context: it says which task the person came
    * to finish. It carries no authority — the backend decides from this
    * browser's own session, so a link forwarded to somebody else does nothing
    * for them. `includeXOrg` is false because the organization is a property
    * of the reference, not of whatever this tab last selected.
    */
   static async redeem(handoffId: string) {
      const data = await apiPost<ResponseSuccess<RedeemedHandoff>>({
         apiPath: `/organizations/onboarding-handoff/${encodeURIComponent(
            handoffId,
         )}/redeem`,
         includeXOrg: false,
      })
      return data || undefined
   }
}
